import {
  Button,
  Combobox,
  SegmentedControl,
  SidePanel,
  StatusText,
  formatCivilDate,
  formatCurrency,
  type StatusTone,
} from '@hue/design-system';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { ErrorCard, LoadingCard, SummaryStrip } from '../../app/data-view.js';
import type { CashFlowView, DreView } from '../../app/list-views.js';
import { useOperationalPeriod } from '../../app/operational-period-context.js';
import { PageHeader } from '../../app/page-title.js';
import type { ViewSetter } from '../../app/view-state.js';
import { getApiError } from '../../lib/api.js';
import { ReportExportButton } from './report-export.js';
import { factWhen, gridNumber, percent, realizedOnly } from './report-format.js';
import {
  reportsApi,
  type CashDrill,
  type CashFlowReport,
  type CashRow,
  type DreReport,
  type DreRow,
  type ReportDrill,
  type ReportFact,
  type ReportFactPage,
  type ReportMonthClosing,
} from './reports-api.js';

/// Para onde o detalhe de um valor leva: a fila de classificação, o extrato, ou as despesas
/// e receitas. Quem navega é a casca; o módulo só pede.
export type ReportDestination =
  'classification' | 'finance' | 'payables' | 'receivables' | 'closing';

/// O mês que já passou diz se é número oficial (ADR 0064): fechado, fechado em parte das unidades
/// ou nada, que é prévia.
function closingNote(closing: ReportMonthClosing | undefined): string | null {
  if (closing?.state === 'CLOSED') return closing.changed ? 'fechado · mudou' : 'fechado';
  if (closing?.state === 'PARTIAL')
    return `fechado em ${closing.closedUnits} de ${closing.totalUnits}`;
  return null;
}

const SHORT_MONTHS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];
const MONTH_NAMES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

const monthIndex = (month: string) => Number(month.slice(5, 7)) - 1;
const monthName = (month: string) => `${MONTH_NAMES[monthIndex(month)]} de ${month.slice(0, 4)}`;

const money = (cents: number) => `${cents < 0 ? '−' : ''}${formatCurrency(Math.abs(cents))}`;

const UNIT_SCOPE = 'A unidade escolhida no topo; sem unidade, a soma das que você alcança.';

function yearMonthOf(month: string, today: string): string {
  return month === 'ALL' ? today.slice(0, 7) : month;
}

/// Linha visível: todas as linhas acima dela na árvore estão abertas.
function visibleRows<T extends { key: string; parentKey: string | null }>(
  rows: readonly T[],
  open: ReadonlySet<string>,
): T[] {
  const byKey = new Map(rows.map((row) => [row.key, row]));
  return rows.filter((row) => {
    let parent = row.parentKey;
    while (parent) {
      if (!open.has(parent)) return false;
      parent = byKey.get(parent)?.parentKey ?? null;
    }
    return true;
  });
}

function PreviewNotice({ children }: { children: ReactNode }) {
  return (
    <div className="mb-4 flex flex-col gap-1 rounded-xl border border-surface-border bg-surface-soft px-4 py-3 text-sm sm:flex-row sm:gap-3">
      <StatusText tone="warning">Prévia</StatusText>
      <p className="text-content-muted">{children}</p>
    </div>
  );
}

function RowLabel({
  row,
  open,
  onToggle,
  hint,
}: {
  row: { key: string; label: string; code: string | null; level: number; hasChildren: boolean };
  open: boolean;
  onToggle: () => void;
  hint?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-1.5" style={{ paddingLeft: `${row.level * 16}px` }}>
      {row.hasChildren ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`${open ? 'Fechar' : 'Abrir'} ${row.label}`}
          className="mt-0.5 grid size-5 shrink-0 cursor-pointer place-items-center rounded text-content-muted hover:bg-content/5"
        >
          <ChevronRight
            aria-hidden="true"
            className={`size-4 transition-transform motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}
          />
        </button>
      ) : (
        <span className="size-5 shrink-0" aria-hidden="true" />
      )}
      <span className="min-w-0">
        {row.code ? <span className="mr-1.5 text-xs text-content-faint">{row.code}</span> : null}
        <span>{row.label}</span>
        {hint}
      </span>
    </div>
  );
}

const ROW_TONE: Record<string, string> = {
  GROUP: 'font-semibold bg-surface-soft/60',
  SECTION: 'font-semibold bg-surface-soft/60',
  SUBTOTAL: 'font-bold border-t border-surface-border',
  RESULT: 'font-bold bg-brand-500/10 border-t border-brand-500/40',
  BALANCE: 'font-semibold',
  WARNING: 'text-warning-400',
  INFO: 'text-content-muted',
  PERCENT: 'text-content-muted',
};

function ValueButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="w-full cursor-pointer rounded px-1 text-right underline-offset-2 hover:bg-content/5 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
    >
      {children}
    </button>
  );
}

function ReportTable({
  caption,
  months,
  notes,
  totalLabel,
  children,
}: {
  caption: string;
  months: readonly string[];
  notes: (month: string) => string | null;
  totalLabel: ReactNode;
  children: ReactNode;
}) {
  return (
    // A única rolagem lateral permitida: colunas de mesma natureza (os meses) que não cabem. A
    // coluna dos nomes e a linha dos meses ficam paradas.
    <div className="max-h-[75vh] overflow-auto rounded-xl border border-surface-border bg-surface-card">
      <table className="w-full min-w-max border-separate border-spacing-0 text-sm tabular-nums">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            <th
              scope="col"
              className="sticky left-0 top-0 z-20 min-w-60 max-w-72 border-b border-surface-border bg-surface-card px-3 py-2 text-left text-xs font-semibold text-content/70"
            >
              Valores em R$
            </th>
            {months.map((month) => {
              const note = notes(month);
              return (
                <th
                  key={month}
                  scope="col"
                  className="sticky top-0 z-10 min-w-24 border-b border-surface-border bg-surface-card px-3 py-2 text-right align-bottom text-xs font-semibold text-content/70"
                >
                  {SHORT_MONTHS[monthIndex(month)]}
                  {note ? (
                    <span className="block text-[11px] font-medium text-content-faint">{note}</span>
                  ) : null}
                </th>
              );
            })}
            <th
              scope="col"
              className="sticky top-0 z-10 min-w-28 border-b border-l border-surface-border bg-surface-card px-3 py-2 text-right align-bottom text-xs font-semibold text-content/70"
            >
              {totalLabel}
            </th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

const cellBase = 'whitespace-nowrap border-b border-surface-border/60 px-2 py-1.5 text-right';
const labelBase =
  'sticky left-0 z-10 min-w-60 max-w-72 border-b border-surface-border/60 bg-surface-card px-3 py-1.5 text-left';

// ---------------------------------------------------------------------------------------------
// DRE

export function DreReportView({
  view,
  onView,
  today,
  onOpen,
}: {
  view: DreView;
  onView: ViewSetter<DreView>;
  today: string;
  onOpen: (destination: ReportDestination) => void;
}) {
  const period = useOperationalPeriod(today);
  const month = yearMonthOf(period.month, today);
  const report = useQuery({
    queryKey: ['accounting', 'reports', 'dre', month, today],
    queryFn: () => reportsApi.dre(month, today),
    placeholderData: keepPreviousData,
  });
  const [open, setOpen] = useState<Set<string> | null>(null);
  const [detail, setDetail] = useState<{ row: DreRow; month: string } | null>(null);
  const data = report.data;
  const expanded =
    open ?? new Set((data?.rows ?? []).filter((row) => row.kind === 'GROUP').map((row) => row.key));
  const toggle = (key: string) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setOpen(next);
  };

  return (
    <section aria-labelledby="dre-title" className="min-w-0">
      <PageHeader
        trail="Contabilidade gerencial / DRE"
        id="dre-title"
        title="DRE"
        description="Se a empresa dá lucro: receitas e despesas no mês a que pertencem, pagas ou não, organizadas pelo plano de contas."
      />
      <PreviewNotice>
        Mês marcado como fechado é número oficial: vale o que foi guardado no fechamento contábil.
        Os outros meses são prévia e mudam enquanto você classifica e concilia. Para tornar um mês
        oficial, use Contabilidade gerencial › Fechamento. Fechar só o caixa do mês não basta.
      </PreviewNotice>
      {report.isError ? (
        <ErrorCard message={getApiError(report.error)} />
      ) : !data ? (
        <LoadingCard label="Montando o DRE" />
      ) : (
        <>
          <SummaryStrip
            label="Resumo do DRE"
            items={[
              {
                label: 'Receita líquida',
                value: formatCurrency(data.summary.netRevenueCents),
              },
              {
                label: 'Resultado do período',
                value: money(data.summary.resultCents),
                ...(data.summary.resultCents >= 0 ? { tone: 'positive' as const } : {}),
              },
              {
                label: 'Margem',
                value: percent(data.summary.marginRatio) || '—',
              },
              {
                label: 'Sem conta do plano',
                value: formatCurrency(data.summary.noAccountCents),
                ...(data.summary.noAccountCents > 0 ? { tone: 'warning' as const } : {}),
              },
            ]}
          />
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <SegmentedControl
              label="Como mostrar os valores"
              compact
              value={view.show}
              onChange={(show) => onView({ show })}
              options={[
                { value: 'values', label: 'Valores' },
                { value: 'percent', label: '% da receita líquida' },
              ]}
            />
            <span className="flex-1" />
            <ReportExportButton
              report="o DRE"
              scope={[
                `Ano ${data.year}, de janeiro a ${MONTH_NAMES[monthIndex(data.months[data.months.length - 1]!)]}`,
                view.show === 'percent' ? 'Em % da receita líquida' : 'Valores em R$',
                UNIT_SCOPE,
              ]}
              load={(format, facts) =>
                reportsApi.exportDre({
                  month,
                  today,
                  format,
                  percent: view.show === 'percent',
                  facts,
                })
              }
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setOpen(new Set(data.rows.filter((row) => row.hasChildren).map((row) => row.key)))
              }
            >
              Abrir todas as contas do plano
            </Button>
            <Button size="sm" variant="outline" onClick={() => setOpen(new Set())}>
              Só os grupos
            </Button>
          </div>
          <DreTable
            report={data}
            percentOfRevenue={view.show === 'percent'}
            expanded={expanded}
            onToggle={toggle}
            onCell={(row, index) => setDetail({ row, month: data.months[index]! })}
          />
          <p className="mt-3 text-xs text-content-muted">
            Clique num valor para ver os lançamentos que o formam. Retiradas, aportes, empréstimos e
            transferências entre contas financeiras não são lucro nem despesa: ficam no Fluxo de
            caixa. O percentual classificado mede quanto do que está lançado já tem conta do plano;
            não diz se falta lançar alguma coisa.
          </p>
        </>
      )}
      {detail && detail.row.drill ? (
        <FactsPanel
          title={detail.row.label.replace(/^[(=±−)\s]+/u, '')}
          subtitle={monthName(detail.month)}
          load={() => reportsApi.dreFacts(detail.month, detail.row.drill!)}
          queryKey={['accounting', 'reports', 'dre-facts', detail.month, detail.row.key]}
          note={dreNote(detail.row.drill)}
          destination={
            detail.row.drill.kind === 'WAITING'
              ? 'finance'
              : detail.row.drill.kind === 'ADJUSTMENTS'
                ? 'closing'
                : 'classification'
          }
          onOpen={onOpen}
          onClose={() => setDetail(null)}
        />
      ) : null}
    </section>
  );
}

function dreNote(drill: ReportDrill): string {
  switch (drill.kind) {
    case 'NONE':
      return 'Estes lançamentos pertencem a este mês e já estão somados ao resultado, mas ninguém disse de que são. Enquanto a linha Sem conta do plano tiver valor, o total está certo e a divisão por linhas ainda não. Escolha a conta do plano em Classificar lançamentos.';
    case 'WAITING':
      return 'Dinheiro que passou pelo extrato e ainda não foi ligado a uma despesa ou receita, nem declarado sem compromisso. Pagamento sem conferência nunca vira despesa: concilie na aba Conciliação de Caixa e extrato ou declare sem compromisso.';
    case 'OFF_SOURCE':
      return 'Despesas e receitas lançadas por um módulo que a empresa não usa mais. Elas ficam fora do resultado e voltam a contar se o módulo for contratado de novo.';
    case 'ADJUSTMENTS':
      return 'Correções de um mês que já estava fechado. O mês fechado continua com os números que foram enviados; a diferença aparece aqui, no mês em que alguém lançou o ajuste, e não entra no resultado deste mês. Cada ajuste tem o motivo e quem o lançou.';
    default:
      return 'Despesa ou receita entra no mês a que pertence, paga ou não: o pagamento muda o caixa, nunca o DRE. Dinheiro do extrato só entra aqui depois de conciliado com uma despesa ou receita, ou declarado sem compromisso.';
  }
}

function DreTable({
  report,
  percentOfRevenue,
  expanded,
  onToggle,
  onCell,
}: {
  report: DreReport;
  percentOfRevenue: boolean;
  expanded: ReadonlySet<string>;
  onToggle: (key: string) => void;
  onCell: (row: DreRow, index: number) => void;
}) {
  const net = report.rows.find((row) => row.key === 'net');
  const shown = visibleRows(report.rows, expanded);
  const display = (row: DreRow, value: number | null, base: number | null | undefined) => {
    if (row.kind === 'PERCENT') return percent(value);
    if (percentOfRevenue && row.kind !== 'INFO' && value !== null)
      return base ? percent(value / base) : '';
    return gridNumber(value);
  };
  const negative = (row: DreRow, value: number | null) =>
    (row.kind === 'SUBTOTAL' || row.kind === 'RESULT') && value !== null && value <= -50
      ? 'text-danger-400'
      : '';
  return (
    <ReportTable
      caption={`DRE de ${report.year}`}
      months={report.months}
      notes={(month) =>
        month === report.currentMonth
          ? 'em andamento'
          : report.currentMonth && month > report.currentMonth
            ? 'só o lançado'
            : closingNote(report.closing[report.months.indexOf(month)])
      }
      totalLabel={
        <>
          Total
          <span className="block text-[11px] font-medium text-content-faint">
            {report.months.length === 12
              ? report.year
              : `jan a ${SHORT_MONTHS[report.months.length - 1]!.toLowerCase()}`}
          </span>
        </>
      }
    >
      {shown.map((row) => {
        if (row.kind === 'SECTION') {
          return (
            <tr key={row.key}>
              <th
                scope="rowgroup"
                colSpan={report.months.length + 2}
                className="sticky left-0 border-b border-surface-border bg-surface-card px-3 pb-1.5 pt-5 text-left text-[11px] font-semibold uppercase tracking-[.1em] text-content-faint"
              >
                {row.label}
              </th>
            </tr>
          );
        }
        const tone = ROW_TONE[row.kind] ?? '';
        return (
          <tr key={row.key} className={tone}>
            <th scope="row" className={`${labelBase} font-[inherit] ${tone}`}>
              <RowLabel
                row={row}
                open={expanded.has(row.key)}
                onToggle={() => onToggle(row.key)}
                hint={
                  row.kind === 'WARNING' ? (
                    <span className="block text-xs font-normal text-content-muted">
                      Já somado ao resultado. Escolha a conta do plano para saber em que linha
                      entra.
                    </span>
                  ) : null
                }
              />
            </th>
            {row.values.map((value, index) => {
              const text = display(row, value, net?.values[index]);
              const clickable = row.drill && value !== null && Math.abs(value) >= 1;
              return (
                <td key={report.months[index]} className={`${cellBase} ${negative(row, value)}`}>
                  {clickable ? (
                    <ValueButton
                      label={`${row.label}, ${monthName(report.months[index]!)}: ver lançamentos`}
                      onClick={() => onCell(row, index)}
                    >
                      {text}
                    </ValueButton>
                  ) : (
                    text
                  )}
                </td>
              );
            })}
            <td
              className={`${cellBase} border-l border-surface-border ${negative(row, row.total)}`}
            >
              {row.kind === 'PERCENT' && row.key === 'classified'
                ? ''
                : display(row, row.total, net?.total)}
            </td>
          </tr>
        );
      })}
    </ReportTable>
  );
}

// ---------------------------------------------------------------------------------------------
// Fluxo de caixa

function cashNote(drill: CashDrill): string {
  if (drill.kind === 'NONE') {
    return 'Dinheiro que já está no saldo, mas ainda sem conta do plano. Escolha a conta do plano em Classificar lançamentos, aba Extrato. Se houver regra para ele, a próxima importação já resolve sozinha.';
  }
  if (drill.kind === 'OVERDUE') {
    return drill.direction === 'INFLOW'
      ? 'Receitas que já venceram e não têm recebimento conciliado. Ficam no mês atual porque o dinheiro ainda pode entrar. Confira se o cliente pagou por outro meio; se pagou, concilie o recebimento.'
      : 'Despesas que já venceram e não têm pagamento conciliado. Ficam no mês atual porque o dinheiro ainda vai sair. Antes de pagar, confira na Conciliação se ela já não foi paga e ficou sem ligar ao extrato.';
  }
  return drill.direction === 'INFLOW'
    ? 'O realizado vem do extrato. O previsto de entrada traz só o que já existe: receitas em aberto. Atendimentos que ainda vão acontecer não entram, e o sistema não estima por média.'
    : 'O realizado vem do extrato. O previsto de saída vem das despesas em aberto e das recorrências, inclusive as que o sistema ainda vai gerar nos próximos meses.';
}

export function CashFlowReportView({
  view,
  onView,
  today,
  onOpen,
}: {
  view: CashFlowView;
  onView: ViewSetter<CashFlowView>;
  today: string;
  onOpen: (destination: ReportDestination) => void;
}) {
  const period = useOperationalPeriod(today);
  const year = Number(yearMonthOf(period.month, today).slice(0, 4));
  const account = view.account || null;
  const withPlanned = view.mode === 'all';
  const accounts = useQuery({
    queryKey: ['accounting', 'reports', 'finance-accounts'],
    queryFn: reportsApi.financeAccounts,
  });
  const report = useQuery({
    queryKey: ['accounting', 'reports', 'cash-flow', year, today, account],
    queryFn: () => reportsApi.cashFlow(year, today, account),
    placeholderData: keepPreviousData,
  });
  const data = useMemo(
    () => (report.data ? (withPlanned ? report.data : realizedOnly(report.data)) : undefined),
    [report.data, withPlanned],
  );
  const [open, setOpen] = useState<Set<string> | null>(null);
  const [detail, setDetail] = useState<{ row: CashRow; month: string } | null>(null);
  const expanded = open ?? new Set(['in', 'out', 'in:overdue', 'out:overdue']);
  const toggle = (key: string) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setOpen(next);
  };
  const accountOptions = [
    { value: '', label: 'Todas as contas financeiras' },
    ...(accounts.data ?? []).map((one) => ({
      value: one.id,
      label: one.unitName ? `${one.name} · ${one.unitName}` : one.name,
    })),
  ];
  const selected = accountOptions.find((one) => one.value === (account ?? ''))?.label ?? '';
  const currentYear = Number(today.slice(0, 4));

  return (
    <section aria-labelledby="cash-flow-title" className="min-w-0">
      <PageHeader
        trail="Contabilidade gerencial / Fluxo de caixa"
        id="cash-flow-title"
        title="Fluxo de caixa"
        description="De onde veio e para onde foi o dinheiro, mês a mês, por conta do plano. O que já aconteceu vem do extrato; o que ainda vai acontecer vem das contas em aberto e das recorrências."
      />
      <PreviewNotice>
        O realizado é o extrato importado; o previsto é o que já está lançado para vencer. Nada é
        estimado por média. Mês marcado como fechado já passou pelo fechamento contábil: o realizado
        dele não muda sem reabrir.
      </PreviewNotice>
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <Combobox
          className="w-full sm:w-72"
          label="Conta financeira"
          placeholder="Todas as contas financeiras"
          selectedLabel={selected}
          options={accountOptions}
          onSelect={(choice) => {
            if (choice.kind === 'option') onView({ account: choice.value });
          }}
          onClear={() => onView({ account: '' })}
          clearable={account !== null}
        />
        <SegmentedControl
          label="O que mostrar"
          compact
          value={view.mode}
          onChange={(mode) => onView({ mode })}
          options={[
            { value: 'all', label: 'Realizado e previsto' },
            { value: 'realized', label: 'Só realizado' },
          ]}
        />
        <span className="flex-1" />
        <ReportExportButton
          report="o Fluxo de caixa"
          scope={[
            `Ano ${year}`,
            selected || 'Todas as contas financeiras',
            withPlanned ? 'Realizado e previsto' : 'Só realizado',
            UNIT_SCOPE,
          ]}
          load={(format, facts) =>
            reportsApi.exportCashFlow({
              year,
              today,
              format,
              planned: withPlanned,
              facts,
              financeAccountId: account,
            })
          }
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            setOpen(
              new Set((data?.rows ?? []).filter((row) => row.hasChildren).map((row) => row.key)),
            )
          }
        >
          Abrir todas as contas do plano
        </Button>
        <Button size="sm" variant="outline" onClick={() => setOpen(new Set(['in', 'out']))}>
          Só entradas e saídas
        </Button>
      </div>
      {report.isError ? (
        <ErrorCard message={getApiError(report.error)} />
      ) : !data ? (
        <LoadingCard label="Montando o Fluxo de caixa" />
      ) : (
        <>
          <SummaryStrip
            label="Resumo do Fluxo de caixa"
            items={[
              {
                label:
                  year === currentYear
                    ? `Saldo hoje, ${formatCivilDate(today).slice(0, 5)}`
                    : year < currentYear
                      ? `Saldo em 31/12/${year}`
                      : 'Saldo previsto em 1º/1',
                value: money(data.summary.balanceNowCents),
              },
              withPlanned && year >= currentYear
                ? {
                    label: `Saldo previsto em 31/12`,
                    value: money(data.summary.balanceEndCents),
                  }
                : {
                    label: 'Geração de caixa no ano',
                    value: money(data.rows.find((row) => row.key === 'generation')!.total.realized),
                  },
              data.summary.lowest
                ? {
                    label: 'Menor saldo previsto',
                    value: money(data.summary.lowest.cents),
                    ...(data.summary.lowest.cents < 0 ? { tone: 'warning' as const } : {}),
                  }
                : {
                    label: 'Entradas no ano',
                    value: money(data.rows.find((row) => row.key === 'in')!.total.realized),
                  },
              {
                label: 'Movimentos sem conta do plano',
                value: formatCurrency(data.summary.noAccountCents),
                ...(data.summary.noAccountCents > 0 ? { tone: 'warning' as const } : {}),
              },
            ]}
          />
          <CashTable
            report={data}
            withPlanned={withPlanned}
            today={today}
            expanded={expanded}
            onToggle={toggle}
            onCell={(row, index) => setDetail({ row, month: data.months[index]! })}
          />
          <p className="mt-3 text-xs text-content-muted">
            Clique num valor para ver os movimentos do extrato ou as despesas e receitas que o
            formam. Todo centavo do extrato está numa linha: o que ainda não tem conta do plano fica
            em Sem conta do plano, e a soma continua batendo com o banco. Transferências entre
            contas financeiras da empresa aparecem nas duas pontas e se anulam com todas as contas
            financeiras somadas.
            {withPlanned && data.summary.variableRecurrences > 0
              ? ` ${data.summary.variableRecurrences === 1 ? 'Uma despesa recorrente de valor variável ficou' : `${data.summary.variableRecurrences} despesas recorrentes de valor variável ficaram`} fora do previsto, porque o valor só se sabe quando a conta do mês é lançada.`
              : ''}
          </p>
        </>
      )}
      {detail && detail.row.drill ? (
        <FactsPanel
          title={
            detail.row.drill.kind === 'OVERDUE'
              ? detail.row.drill.direction === 'INFLOW'
                ? 'Vencidas e ainda não recebidas'
                : 'Vencidas e ainda não pagas'
              : detail.row.label
          }
          subtitle={
            detail.row.drill.kind === 'OVERDUE' && detail.row.drill.dueMonth
              ? `Que ${detail.row.label}`
              : monthName(detail.month)
          }
          load={() =>
            reportsApi.cashFacts({
              month: detail.month,
              drill: detail.row.drill!,
              today,
              planned: withPlanned,
              financeAccountId: account,
            })
          }
          queryKey={[
            'accounting',
            'reports',
            'cash-facts',
            detail.month,
            detail.row.key,
            withPlanned,
            account,
          ]}
          note={cashNote(detail.row.drill)}
          destination={
            detail.row.drill.kind === 'OVERDUE'
              ? detail.row.drill.direction === 'INFLOW'
                ? 'receivables'
                : 'payables'
              : 'classification'
          }
          today={today}
          onOpen={onOpen}
          onClose={() => setDetail(null)}
        />
      ) : null}
    </section>
  );
}

function CashTable({
  report,
  withPlanned,
  today,
  expanded,
  onToggle,
  onCell,
}: {
  report: CashFlowReport;
  withPlanned: boolean;
  today: string;
  expanded: ReadonlySet<string>;
  onToggle: (key: string) => void;
  onCell: (row: CashRow, index: number) => void;
}) {
  const shown = visibleRows(report.rows, expanded);
  const day = formatCivilDate(today).slice(0, 5);
  return (
    <ReportTable
      caption={`Fluxo de caixa de ${report.year}`}
      months={report.months}
      notes={(month) =>
        month === report.currentMonth
          ? withPlanned
            ? `até ${day} + previsto`
            : `até ${day}`
          : month > today.slice(0, 7)
            ? withPlanned
              ? 'previsto'
              : null
            : closingNote(report.closing[report.months.indexOf(month)])
      }
      totalLabel={
        <>
          Total
          <span className="block text-[11px] font-medium text-content-faint">{report.year}</span>
        </>
      }
    >
      {shown.map((row) => {
        const tone = ROW_TONE[row.kind] ?? '';
        const balance = row.kind === 'BALANCE' || row.kind === 'RESULT';
        return (
          <tr key={row.key} className={tone}>
            <th scope="row" className={`${labelBase} font-[inherit] ${tone}`}>
              <RowLabel
                row={row}
                open={expanded.has(row.key)}
                onToggle={() => onToggle(row.key)}
                hint={
                  row.outsideDre && row.kind === 'GROUP' ? (
                    <span className="ml-2 text-xs font-normal text-content-muted">fora do DRE</span>
                  ) : null
                }
              />
            </th>
            {row.cells.map((cell, index) => {
              const value = cell.realized + cell.planned;
              const planned =
                !balance && cell.planned !== 0
                  ? cell.realized === 0
                    ? 'bg-info/10 italic text-info'
                    : 'bg-info/5'
                  : '';
              const negative =
                (row.kind === 'SUBTOTAL' || balance) && value <= -50 ? 'text-danger-400' : '';
              const clickable = row.drill && Math.abs(value) >= 1;
              const text = gridNumber(value);
              return (
                <td key={report.months[index]} className={`${cellBase} ${planned} ${negative}`}>
                  {clickable ? (
                    <ValueButton
                      label={`${row.label}, ${monthName(report.months[index]!)}: ver lançamentos`}
                      onClick={() => onCell(row, index)}
                    >
                      {text}
                    </ValueButton>
                  ) : (
                    text
                  )}
                </td>
              );
            })}
            <td className={`${cellBase} border-l border-surface-border`}>
              {gridNumber(row.total.realized + row.total.planned)}
            </td>
          </tr>
        );
      })}
    </ReportTable>
  );
}

// ---------------------------------------------------------------------------------------------
// Detalhe de um valor

const ORIGIN_LABELS: Record<ReportFact['origin'], (cents: number) => string> = {
  OBLIGATION: (cents) => (cents < 0 ? 'Despesa' : 'Receita'),
  TRANSFER: () => 'Transferência entre contas próprias',
  DECLARED: () => 'Declarado sem compromisso',
  DIFFERENCE: () => 'Diferença resolvida',
  WAITING: () => 'Aguardando conciliação',
  SETTLEMENT: (cents) => (cents < 0 ? 'Pagou uma despesa' : 'Recebeu uma receita'),
  RECURRENCE: () => 'Recorrência',
  COUNTED_ONCE: () => 'Contado uma vez',
  ADJUSTMENT: () => 'Ajuste de mês anterior',
};

const STATUS: Record<
  NonNullable<ReportFact['status']>,
  { tone: StatusTone; label: (cents: number) => string }
> = {
  PAID: { tone: 'positive', label: (cents) => (cents < 0 ? 'paga' : 'recebida') },
  OPEN: { tone: 'brand', label: () => 'em aberto' },
  OVERDUE: { tone: 'danger', label: () => 'vencida' },
  NOT_GENERATED: { tone: 'neutral', label: () => 'ainda não gerada' },
};

const DESTINATIONS: Record<ReportDestination, string> = {
  classification: 'Abrir Classificar lançamentos',
  finance: 'Abrir Caixa e extrato',
  payables: 'Abrir Despesas',
  receivables: 'Abrir Receitas',
  closing: 'Abrir Fechamento',
};

function FactsPanel({
  title,
  subtitle,
  load,
  queryKey,
  note,
  destination,
  today,
  onOpen,
  onClose,
}: {
  title: string;
  subtitle: string;
  load: () => Promise<ReportFactPage>;
  queryKey: readonly unknown[];
  note: string;
  destination: ReportDestination;
  today?: string;
  onOpen: (destination: ReportDestination) => void;
  onClose: () => void;
}) {
  const facts = useQuery({ queryKey, queryFn: load });
  return (
    <SidePanel
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={title}
      description={subtitle}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-content-muted">
            {facts.data
              ? facts.data.total > facts.data.rows.length
                ? `Mostrando os ${facts.data.rows.length} maiores de ${facts.data.total} lançamentos`
                : `${facts.data.total} ${facts.data.total === 1 ? 'lançamento' : 'lançamentos'}`
              : ''}
          </span>
          <Button size="sm" onClick={() => onOpen(destination)}>
            {DESTINATIONS[destination]}
          </Button>
        </div>
      }
    >
      {facts.isError ? (
        <ErrorCard message={getApiError(facts.error)} />
      ) : !facts.data ? (
        <LoadingCard label="Abrindo os lançamentos" />
      ) : (
        <div className="grid gap-4">
          <p className="text-2xl font-bold tabular-nums">{money(facts.data.totalCents)}</p>
          <ul className="divide-y divide-surface-border">
            {facts.data.rows.map((fact, index) => (
              <li
                key={`${fact.date}-${fact.description}-${index}`}
                className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 py-3"
              >
                <span className="min-w-0 break-words">{fact.description}</span>
                <strong className="text-right tabular-nums">{money(fact.cents)}</strong>
                <span className="col-span-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-content-muted">
                  <span>{ORIGIN_LABELS[fact.origin](fact.cents)}</span>
                  {fact.status ? (
                    <StatusText tone={STATUS[fact.status].tone}>
                      {STATUS[fact.status].label(fact.cents)}
                    </StatusText>
                  ) : null}
                  <span>{factWhen(fact, today)}</span>
                  {fact.detail ? <span>· {fact.detail}</span> : null}
                  {fact.unitName ? <span>· {fact.unitName}</span> : null}
                  {fact.account ? (
                    <span>
                      · {fact.account.code} {fact.account.name}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
          <p className="rounded-xl bg-surface-soft p-3 text-sm text-content-muted">{note}</p>
        </div>
      )}
    </SidePanel>
  );
}
